'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import {
  PermissionSet,
  PermissionAction,
  buildPermissionSet,
  createPermissionHelpers,
  DEFAULT_ROLE_PERMISSIONS,
  hrefToModuleKey,
} from '@/lib/permissions';

export type UserRole = 'admin' | 'manager' | 'viewer';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  roleId: string | null;
  department: string;
  jobTitle: string;
  phone: string;
  username: string;
  avatarUrl: string;
  isActive: boolean;
  twoFactorEnabled: boolean;
  notificationEmail: boolean;
  notificationApp: boolean;
  themePreference: string;
  languagePreference: string;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  forcePasswordChange: boolean;
  dataAccessLevel: string;
  createdBy: string | null;
}

// Legacy role-based module access (kept for backward compat)
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  admin: [
    '/', '/start', '/profit-allocation-strategic-pools', '/scenario-forecast-analysis',
    '/ai-scenario-advisor', '/risk-management-dashboard',
    '/pools/growth-development', '/pools/investment-wealth', '/pools/reserve-protection',
    '/pools/innovation-opportunity', '/pools/team-reward-recognition',
    '/pool-banking', '/pool-banking/bank-registry',
    '/portfolio-investment-management', '/team-compensation-reserves',
    '/admin-configuration', '/planning-tracking',
    '/user-profile', '/admin/user-management',
  ],
  manager: [
    '/', '/start', '/profit-allocation-strategic-pools', '/scenario-forecast-analysis',
    '/ai-scenario-advisor', '/risk-management-dashboard',
    '/pools/growth-development', '/pools/investment-wealth', '/pools/reserve-protection',
    '/pools/innovation-opportunity', '/pools/team-reward-recognition',
    '/pool-banking', '/pool-banking/bank-registry',
    '/portfolio-investment-management', '/team-compensation-reserves',
    '/planning-tracking', '/user-profile',
  ],
  viewer: [
    '/', '/start', '/profit-allocation-strategic-pools', '/scenario-forecast-analysis',
    '/risk-management-dashboard',
    '/pools/growth-development', '/pools/investment-wealth', '/pools/reserve-protection',
    '/pools/innovation-opportunity', '/pools/team-reward-recognition',
    '/user-profile',
  ],
};

export function canAccess(role: UserRole | undefined, path: string): boolean {
  if (!role) return false;
  const allowed = ROLE_PERMISSIONS[role] || [];
  return allowed.some((p) => path === p || path.startsWith(p + '/'));
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  permissionSet: PermissionSet;
  permissions: ReturnType<typeof createPermissionHelpers>;
  canAccessModule: (moduleKey: string) => boolean;
  canAccessRoute: (href: string) => boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  logAudit: (action: string, module?: string, oldVal?: any, newVal?: any, notes?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function mapProfile(data: any): UserProfile {
  return {
    id: data.id,
    email: data.email,
    fullName: data.full_name || '',
    role: data.role as UserRole,
    roleId: data.role_id || null,
    department: data.department || '',
    jobTitle: data.job_title || '',
    phone: data.phone || '',
    username: data.username || '',
    avatarUrl: data.avatar_url || '',
    isActive: data.is_active ?? true,
    twoFactorEnabled: data.two_factor_enabled ?? false,
    notificationEmail: data.notification_email ?? true,
    notificationApp: data.notification_app ?? true,
    themePreference: data.theme_preference || 'dark',
    languagePreference: data.language_preference || 'en',
    lastLoginAt: data.last_login_at || null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    forcePasswordChange: data.force_password_change ?? false,
    dataAccessLevel: data.data_access_level || 'own',
    createdBy: data.created_by || null,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [permissionSet, setPermissionSet] = useState<PermissionSet>({});
  const supabase = createClient();

  const loadPermissions = useCallback(async (userProfile: UserProfile) => {
    try {
      // Try to load from DB role_permissions
      if (userProfile.roleId) {
        const { data, error } = await supabase
          .from('role_permissions')
          .select('permission_id, granted, permissions(module_key, action)')
          .eq('role_id', userProfile.roleId)
          .eq('granted', true);

        if (!error && data && data.length > 0) {
          const perms = data.map((rp: any) => ({
            module_key: rp.permissions?.module_key || '',
            action: rp.permissions?.action as PermissionAction,
            granted: rp.granted,
          }));
          setPermissionSet(buildPermissionSet(perms));
          return;
        }
      }
      // Fallback to default role-based permissions
      setPermissionSet(DEFAULT_ROLE_PERMISSIONS[userProfile.role] || DEFAULT_ROLE_PERMISSIONS.viewer);
    } catch {
      setPermissionSet(DEFAULT_ROLE_PERMISSIONS[userProfile.role] || DEFAULT_ROLE_PERMISSIONS.viewer);
    }
  }, [supabase]);

  const fetchProfile = useCallback(async (userId: string) => {
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        const mapped = mapProfile(data);
        setProfile(mapped);
        await loadPermissions(mapped);
        await supabase
          .from('user_profiles')
          .update({ last_login_at: new Date().toISOString() })
          .eq('id', userId);
      }
    } catch {
      // profile table may not exist yet
    } finally {
      setProfileLoading(false);
    }
  }, [supabase, loadPermissions]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setPermissionSet({});
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const permissions = createPermissionHelpers(permissionSet);

  const canAccessModule = (moduleKey: string): boolean => {
    // Admin always has access
    if (profile?.role === 'admin') return true;
    return permissions.canView(moduleKey);
  };

  const canAccessRoute = (href: string): boolean => {
    if (profile?.role === 'admin') return true;
    const moduleKey = hrefToModuleKey(href);
    if (!moduleKey) return canAccess(profile?.role, href);
    return canAccessModule(moduleKey);
  };

  const logAudit = async (
    action: string,
    module = '',
    oldVal?: any,
    newVal?: any,
    notes = ''
  ) => {
    try {
      await supabase.from('audit_logs').insert({
        user_id: user?.id || null,
        user_email: profile?.email || '',
        user_name: profile?.fullName || '',
        action,
        module,
        old_value: oldVal ? JSON.stringify(oldVal) : null,
        new_value: newVal ? JSON.stringify(newVal) : null,
        ip_address: '',
        status: 'success',
        notes,
      });
    } catch {
      // non-critical
    }
  };

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'viewer',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setPermissionSet({});
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return mapProfile(data);
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('Not authenticated');
    const dbUpdates: any = {};
    if (updates.fullName !== undefined) dbUpdates.full_name = updates.fullName;
    if (updates.department !== undefined) dbUpdates.department = updates.department;
    if (updates.jobTitle !== undefined) dbUpdates.job_title = updates.jobTitle;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
    if (updates.username !== undefined) dbUpdates.username = updates.username;
    if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;
    if (updates.twoFactorEnabled !== undefined) dbUpdates.two_factor_enabled = updates.twoFactorEnabled;
    if (updates.notificationEmail !== undefined) dbUpdates.notification_email = updates.notificationEmail;
    if (updates.notificationApp !== undefined) dbUpdates.notification_app = updates.notificationApp;
    if (updates.themePreference !== undefined) dbUpdates.theme_preference = updates.themePreference;
    if (updates.languagePreference !== undefined) dbUpdates.language_preference = updates.languagePreference;

    const { error } = await supabase.from('user_profiles').update(dbUpdates).eq('id', user.id);
    if (error) throw error;
    await fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    profileLoading,
    permissionSet,
    permissions,
    canAccessModule,
    canAccessRoute,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    updateProfile,
    refreshProfile,
    logAudit,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
